import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

import { ArrowPathIcon } from '@heroicons/react/24/outline';

function MobileInput({
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={[
        'flex w-full rounded-lg border border-input bg-background px-3 text-sm ring-offset-background',
        'file:border-0 file:bg-transparent file:text-sm file:font-medium',
        'placeholder:text-muted-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'h-12 md:h-9',
        className,
      ].join(' ')}
    />
  );
}

const isIOS = Capacitor.getPlatform() === 'ios';
const showGoogleButton = !isIOS;

export default function SignupPage() {
  const { signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', title: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) {
      toast.error('Please agree to the Terms & Conditions to continue.');
      return;
    }
    setLoading(true);
    try {
      await signup(formData.name, formData.email, formData.password, formData.title);
      navigate('/welcome');
    } catch (err: any) {
      toast.error(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      navigate('/welcome');
    } catch (err: any) {
      toast.error(err.message || 'Google signup failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6">
      <div className="w-full max-w-sm">
        <div className="bg-card border border-border rounded-3xl overflow-hidden">

          {/* Top section — Google button on web/Android, plain header on iOS */}
          <div className={`px-6 pt-6 pb-5 border-b border-border rounded-b-3xl ${showGoogleButton ? 'bg-[#f0f0f0] dark:bg-muted/50' : ''}`}>
            <h1 className="text-xl font-semibold text-foreground mb-0.5">Sign up</h1>
            <p className="text-sm text-muted-foreground mb-4">
              Already have an account?{' '}
              <Link to="/login" className="text-foreground underline font-medium">Sign in</Link>
            </p>

            {showGoogleButton && (
              <Button
                type="button"
                variant="outline"
                mobileFullHeight
                className="w-full gap-2"
                onClick={handleGoogleSignup}
                disabled={googleLoading}
              >
                {googleLoading ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                )}
                Continue with Google
              </Button>
            )}
          </div>

          {/* Form section */}
          <div className="px-6 py-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Stack name/title vertically on mobile, side-by-side on md+ */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Full Name</label>
                  <MobileInput
                    type="text"
                    placeholder="John Doe"
                    autoComplete="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-foreground">Title</label>
                  <MobileInput
                    type="text"
                    placeholder="Architect"
                    autoComplete="organization-title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Email</label>
                <MobileInput
                  type="email"
                  placeholder="your@email.com"
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Password</label>
                <MobileInput
                  type="password"
                  placeholder="••••••••"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                />
                <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
              </div>

              <Checkbox
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                label={
                  <>
                    I agree to the{' '}
                    <a
                      href="https://www.notion.so/oluwatobi-olarewaju/Terms-of-service-2348673c0e40807886bfd541bfa262bf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary underline"
                    >
                      Terms & Conditions
                    </a>
                    {' '}and{' '}
                    <a
                      href="https://planbase.app/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary underline"
                    >
                      Privacy Policy
                    </a>
                  </>
                }
              />

              <Button
                type="submit"
                disabled={loading || !agreedToTerms}
                mobileFullHeight
                className="w-full"
              >
                {loading ? <><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Creating account...</> : 'Create Account'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
